import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  KeyRound, Plus, Trash2, ExternalLink, RefreshCw,
  Plug, Info, ToggleLeft, ToggleRight, Lightbulb, TrendingUp,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/apiClient";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

// Paleta de líneas/barras para hasta 8 tokens (suficiente para uso real)
const TOKEN_PALETTE = [
  "#6366f1", "#10b981", "#f59e0b", "#ef4444",
  "#0ea5e9", "#a855f7", "#14b8a6", "#f97316",
];

// Umbral (req/día) para considerar un token "próximo al techo diario"
const DAILY_TOKEN_CAP = 50000;
const NEAR_CAP_THRESHOLD = 0.8 * DAILY_TOKEN_CAP;

/**
 * Sección "Pool de App Tokens Socrata" para `/admin/secop/config`.
 *
 * Permite:
 * - Ver la lista de tokens con consumo (request_count, error_count, last_used_at)
 * - Agregar un nuevo token (label + valor)
 * - Habilitar/deshabilitar (toggle), renombrar, eliminar y probar cada token
 * - Mostrar un help banner con instrucciones paso a paso + deep-links para
 *   obtener un App Token en datos.gov.co
 *
 * El backend implementa rotación automática: cada consulta SECOP elige el
 * token con menor `request_count` y registra el resultado.
 */
export default function SecopTokenPoolSection({ onPoolChanged } = {}) {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [usage, setUsage] = useState(null);
  const [windowDays, setWindowDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newToken, setNewToken] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const notifyParent = useCallback(() => {
    if (typeof onPoolChanged === "function") onPoolChanged();
  }, [onPoolChanged]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: poolData }, { data: usageData }] = await Promise.all([
        api.get("/admin/secop/tokens"),
        api.get(`/admin/secop/tokens/usage?window_days=${windowDays}`),
      ]);
      setItems(poolData.items || []);
      setTotals(poolData.totals || {});
      setUsage(usageData);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al cargar tokens");
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => { load(); }, [load, reloadTick]);

  const refresh = useCallback(() => setReloadTick((t) => t + 1), []);

  const addToken = useCallback(async () => {
    const label = newLabel.trim();
    const token = newToken.trim();
    if (token.length < 8) {
      toast.error("El token debe tener al menos 8 caracteres");
      return;
    }
    setAdding(true);
    try {
      await api.post("/admin/secop/tokens", { label, token });
      toast.success("Token agregado al pool");
      setNewLabel("");
      setNewToken("");
      refresh();
      notifyParent();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo agregar el token");
    } finally {
      setAdding(false);
    }
  }, [newLabel, newToken, refresh]);

  const toggleDisabled = useCallback(async (it) => {
    setBusyId(it.id);
    try {
      await api.patch(`/admin/secop/tokens/${it.id}`, { disabled: !it.disabled });
      refresh();
      notifyParent();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo actualizar");
    } finally {
      setBusyId(null);
    }
  }, [refresh]);

  const renameToken = useCallback(async (it) => {
    const nextLabel = window.prompt("Nuevo nombre para este token:", it.label || "");
    if (nextLabel == null) return;
    setBusyId(it.id);
    try {
      await api.patch(`/admin/secop/tokens/${it.id}`, { label: nextLabel });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo renombrar");
    } finally {
      setBusyId(null);
    }
  }, [refresh]);

  const deleteToken = useCallback(async (it) => {
    if (!window.confirm(`¿Eliminar el token "${it.label}" del pool? Esta acción no puede deshacerse.`)) return;
    setBusyId(it.id);
    try {
      await api.delete(`/admin/secop/tokens/${it.id}`);
      toast.success("Token eliminado");
      refresh();
      notifyParent();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo eliminar");
    } finally {
      setBusyId(null);
    }
  }, [refresh]);

  const testToken = useCallback(async (it) => {
    setBusyId(it.id);
    try {
      const { data } = await api.post(`/admin/secop/tokens/${it.id}/test`);
      const allOk = data.results?.procesos?.ok && data.results?.contratos?.ok;
      if (allOk) toast.success(`${it.label}: conexión OK`);
      else {
        const err = data.results?.procesos?.error || data.results?.contratos?.error || "falló";
        toast.error(`${it.label}: ${String(err).slice(0, 120)}`);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al probar token");
    } finally {
      setBusyId(null);
    }
  }, [refresh]);

  return (
    <div className="border border-gray-200 bg-white" data-testid="secop-tokens-pool-section">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <KeyRound size={14} className="text-indigo-600" />
          <h2 className="font-serif text-base">Pool de App Tokens Socrata</h2>
          <span className="text-[10px] uppercase tracking-widest text-gray-500">
            rotación por menor consumo
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowHelp((v) => !v)}
            className="rounded-sm h-8 text-xs"
            data-testid="secop-token-help-btn"
          >
            <Lightbulb size={13} className="mr-1.5 text-amber-600" />
            {showHelp ? "Ocultar guía" : "¿Cómo obtener un token?"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={refresh}
            disabled={loading}
            className="rounded-sm h-8 px-2"
            data-testid="secop-tokens-refresh"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      {/* Help / Onboarding banner */}
      {showHelp && (
        <div className="px-4 py-3 border-b border-amber-100 bg-amber-50/60 text-xs space-y-2" data-testid="secop-token-help-banner">
          <div className="flex items-start gap-2 text-gray-700">
            <Info size={14} className="text-amber-700 mt-0.5 shrink-0" />
            <div className="space-y-2">
              <p>
                Sin token: la API datos.gov.co limita a ~<strong>1.000 requests/día</strong> compartidos por IP.
                Con un App Token: ~<strong>50.000 requests/día por token</strong>. Cuantos más tokens registres en este pool,
                mayor el techo combinado y la rotación los usa equitativamente.
              </p>
              <ol className="list-decimal pl-5 space-y-1 text-gray-700">
                <li>
                  Crea una cuenta gratis en{" "}
                  <a
                    href="https://www.datos.gov.co/signup"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-0.5"
                    data-testid="secop-token-signup-link"
                  >
                    datos.gov.co/signup <ExternalLink size={10} />
                  </a>{" "}
                  (también sirve <a href="https://evergreen.data.socrata.com/signup" target="_blank" rel="noreferrer" className="text-primary hover:underline">cuenta Socrata global ↗</a>).
                </li>
                <li>
                  Inicia sesión y entra a{" "}
                  <a
                    href="https://www.datos.gov.co/profile/edit/developer_settings"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-0.5"
                    data-testid="secop-token-devsettings-link"
                  >
                    Developer Settings <ExternalLink size={10} />
                  </a>.
                </li>
                <li>Sección <strong>&ldquo;App Tokens&rdquo;</strong> → <em>Create New App Token</em>. Nombre libre (ej: <code>JustSapiens Procure</code>).</li>
                <li>
                  Copia el valor bajo el rótulo <strong>&ldquo;App Token&rdquo;</strong> y pégalo abajo.
                  <span className="block mt-1 px-2 py-1 bg-rose-50 border border-rose-200 rounded-sm text-[10.5px] text-rose-800">
                    <strong>⚠ NO confundir:</strong> <em>App Tokens</em> y <em>API Keys</em> son cosas distintas.
                    <ul className="list-disc pl-4 mt-0.5">
                      <li><strong>App Token</strong> (lo que necesitamos): cadena pública ~32 caracteres. Header <code>X-App-Token</code>.</li>
                      <li><strong>API Key / Authentication Key</strong>: par <em>Key ID + Key Secret</em> para HTTP Basic Auth de usuario. <strong>NO sirve aquí</strong> — Socrata responde <code>403 &quot;Invalid app_token&quot;</code> si pegas el Key ID.</li>
                    </ul>
                  </span>
                </li>
                <li>Repite para crear 2-3 tokens y maximizar el techo diario combinado.</li>
              </ol>
              <p className="text-gray-600 italic">
                Tip: si un token empieza a dar errores 403, deshabilítalo aquí mismo — la rotación lo saltará automáticamente.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2 border-b border-gray-100 text-xs">
        <KpiInline label="Tokens" value={totals.count ?? 0} testid="secop-tokens-kpi-count" />
        <KpiInline label="Activos" value={totals.active ?? 0} testid="secop-tokens-kpi-active" />
        <KpiInline label="Requests acumulados" value={totals.total_requests ?? 0} testid="secop-tokens-kpi-requests" />
        <KpiInline label="Errores acumulados" value={totals.total_errors ?? 0} testid="secop-tokens-kpi-errors" warn={(totals.total_errors ?? 0) > 0} />
      </div>

      {/* Gráfico temporal de consumo (7 / 30 días) */}
      {usage && usage.tokens && usage.tokens.length > 0 && (
        <UsageChart usage={usage} windowDays={windowDays} setWindowDays={setWindowDays} />
      )}

      {/* Lista de tokens */}
      {items.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500" data-testid="secop-tokens-empty">
          Aún no has registrado tokens. La API SECOP se consultará sin token (límite ~1.000 req/día).
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="secop-tokens-table">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Etiqueta</th>
                <th className="px-3 py-2 text-left">Token</th>
                <th className="px-3 py-2 text-right">Requests</th>
                <th className="px-3 py-2 text-right">Errores</th>
                <th className="px-3 py-2 text-left">Último uso</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const errRate = it.request_count > 0 ? (it.error_count / it.request_count) : 0;
                const errBad = errRate > 0.5 && it.request_count >= 4;
                return (
                  <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`secop-token-row-${i}`}>
                    <td className="px-3 py-2 text-gray-800">
                      <button
                        type="button"
                        onClick={() => renameToken(it)}
                        className="hover:underline text-left"
                        title="Renombrar"
                        data-testid={`secop-token-rename-${i}`}
                      >
                        {it.label || <span className="italic text-gray-400">sin nombre</span>}
                      </button>
                      {it.last_error && (
                        <div className="text-[10px] text-rose-600 truncate max-w-[200px]" title={it.last_error}>
                          último error: {String(it.last_error).slice(0, 60)}{String(it.last_error).length > 60 ? "…" : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-gray-600">{it.token_masked}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{it.request_count}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${errBad ? "text-rose-600 font-medium" : "text-gray-600"}`}>
                      {it.error_count}
                    </td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                      {it.last_used_at ? new Date(it.last_used_at).toLocaleString("es-CO") : <span className="italic text-gray-400">nunca</span>}
                    </td>
                    <td className="px-3 py-2">
                      {it.disabled ? (
                        <span className="px-1.5 py-0.5 border rounded-sm text-[10px] bg-gray-100 border-gray-200 text-gray-600">
                          Deshabilitado
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 border rounded-sm text-[10px] bg-emerald-50 border-emerald-200 text-emerald-800">
                          Activo
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => testToken(it)}
                        disabled={busyId === it.id}
                        className="h-7 px-2 text-[11px] mr-1"
                        title="Probar token"
                        data-testid={`secop-token-test-${i}`}
                      >
                        <Plug size={11} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleDisabled(it)}
                        disabled={busyId === it.id}
                        className="h-7 px-2 text-[11px] mr-1"
                        title={it.disabled ? "Habilitar" : "Deshabilitar"}
                        data-testid={`secop-token-toggle-${i}`}
                      >
                        {it.disabled ? <ToggleLeft size={13} className="text-gray-500" /> : <ToggleRight size={13} className="text-emerald-600" />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteToken(it)}
                        disabled={busyId === it.id}
                        className="h-7 px-2 text-[11px] text-rose-600 hover:bg-rose-50"
                        title="Eliminar"
                        data-testid={`secop-token-delete-${i}`}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add new token form */}
      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50/50" data-testid="secop-token-add-form">
        <div className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="flex-1">
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Etiqueta</label>
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Ej: Token principal"
              className="h-9 text-xs rounded-sm"
              maxLength={80}
              data-testid="secop-token-label-input"
            />
          </div>
          <div className="flex-[2]">
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">App Token Socrata</label>
            <Input
              type="password"
              value={newToken}
              onChange={(e) => setNewToken(e.target.value)}
              placeholder="Pega aquí tu App Token (mínimo 8 caracteres)"
              className="h-9 text-xs rounded-sm font-mono"
              data-testid="secop-token-value-input"
            />
          </div>
          <Button
            type="button"
            onClick={addToken}
            disabled={adding || newToken.trim().length < 8}
            className="rounded-sm h-9 text-xs"
            data-testid="secop-token-add-btn"
          >
            <Plus size={13} className="mr-1.5" />
            {adding ? "Agregando…" : "Agregar al pool"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function KpiInline({ label, value, testid, warn = false }) {
  return (
    <div className="flex items-center justify-between border border-gray-200 bg-gray-50/40 px-2 py-1.5" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-widest text-gray-500">{label}</span>
      <span className={`font-mono tabular-nums ${warn ? "text-rose-600 font-medium" : "text-gray-800"}`}>{value}</span>
    </div>
  );
}


function UsageChart({ usage, windowDays, setWindowDays }) {
  const { tokens = [], totals_by_date = [], dates = [] } = usage;

  // Filtrar tokens sin actividad en la ventana para no saturar la leyenda
  const activeTokens = useMemo(
    () => tokens.filter((t) => t.window_total > 0),
    [tokens],
  );

  // Recharts requiere shape `[{date, [tokenId]: requests, ...}, ...]`
  const chartData = useMemo(() => {
    return dates.map((d, i) => {
      const row = { date: d.slice(5), full_date: d, total: totals_by_date[i]?.requests || 0 };
      activeTokens.forEach((t) => {
        const pt = t.daily.find((x) => x.date === d);
        row[t.id] = pt?.requests || 0;
      });
      return row;
    });
  }, [dates, activeTokens, totals_by_date]);

  // Detectar tokens próximos al techo diario
  const nearCapToday = useMemo(() => {
    const today = dates[dates.length - 1];
    if (!today) return [];
    return activeTokens
      .map((t) => {
        const pt = t.daily.find((x) => x.date === today);
        return { ...t, today_requests: pt?.requests || 0 };
      })
      .filter((t) => t.today_requests >= NEAR_CAP_THRESHOLD);
  }, [activeTokens, dates]);

  const allEmpty = activeTokens.length === 0;

  return (
    <div className="px-4 py-3 border-b border-gray-100" data-testid="secop-tokens-usage-chart">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <TrendingUp size={13} className="text-indigo-600" />
          <span className="text-[11px] uppercase tracking-widest text-gray-600">
            Consumo diario · últimos {windowDays} días
          </span>
        </div>
        <div className="flex items-center gap-1 text-[11px]" role="radiogroup">
          {[7, 30].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setWindowDays(d)}
              className={`px-2 py-0.5 border rounded-sm ${
                windowDays === d
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
              data-testid={`secop-usage-window-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {nearCapToday.length > 0 && (
        <div className="mb-2 px-2 py-1.5 border border-rose-200 bg-rose-50 text-[11px] text-rose-800 rounded-sm" data-testid="secop-usage-near-cap">
          <strong>⚠ Atención:</strong> {nearCapToday.map((t) => t.label).join(", ")} llegó al 80% del techo diario
          (~{DAILY_TOKEN_CAP.toLocaleString("es-CO")} req). Considera agregar otro token al pool.
        </div>
      )}

      {allEmpty ? (
        <div className="text-[11px] text-gray-500 py-6 text-center" data-testid="secop-usage-empty">
          Aún no hay consumo registrado en esta ventana. Cuando ejecutes búsquedas SECOP, las verás aquí.
        </div>
      ) : (
        <div style={{ width: "100%", height: 200 }} data-testid="secop-usage-chart-canvas">
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 11, padding: 6 }}
                labelFormatter={(_, payload) =>
                  payload && payload[0] ? payload[0].payload.full_date : ""
                }
              />
              <Legend wrapperStyle={{ fontSize: 10, paddingTop: 4 }} iconSize={8} />
              {activeTokens.map((t, i) => (
                <Bar
                  key={t.id}
                  dataKey={t.id}
                  name={t.label || t.id.slice(0, 6)}
                  stackId="reqs"
                  fill={TOKEN_PALETTE[i % TOKEN_PALETTE.length]}
                  radius={i === activeTokens.length - 1 ? [2, 2, 0, 0] : 0}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-[10px]">
        {activeTokens.map((t, i) => (
          <div
            key={t.id}
            className="flex items-center gap-1.5 border border-gray-100 px-1.5 py-1 rounded-sm bg-gray-50/30"
            data-testid={`secop-usage-token-${i}`}
          >
            <span
              className="inline-block w-2 h-2 rounded-sm"
              style={{ backgroundColor: TOKEN_PALETTE[i % TOKEN_PALETTE.length] }}
            />
            <span className="text-gray-700 truncate">{t.label || "(sin nombre)"}</span>
            <span className="ml-auto font-mono tabular-nums text-gray-600">{t.window_total}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
